import { useState, useCallback, useEffect, useRef, useMemo } from "react"
import { ELEMENT_TYPE } from "types/ReportingDashboard/elements.types"
import { QueryInstance } from "types/engine.types"

/**
 * Common element state interface
 */
export interface ElementState<TData = unknown> {
  /** Element data from engine queries */
  data: TData | null
  /** Loading state */
  isLoading: boolean
  /** Error state */
  error: string | null
  /** Whether element has attempted to load data */
  hasAttemptedLoad: boolean
  /** Whether element has processed data */
  hasProcessedData: boolean
  /** Whether element is queued for execution */
  isQueuedForExecution: boolean
}

/**
 * Common element configuration interface
 */
export interface ElementConfigState {
  /** Whether element shows selection UI */
  showSelects: boolean
  /** Whether element is initializing */
  isInitializing: boolean
  /** Whether to skip next config update */
  skipNextConfigUpdate: boolean
}

/**
 * Auto-run configuration
 */
export interface AutoRunConfig {
  /** Enable auto-run functionality */
  enabled: boolean
  /** Delay before auto-run (ms) */
  delay: number
  /** Maximum auto-run attempts */
  maxAttempts: number
  /** Conditions that must be met for auto-run */
  conditions: () => boolean
}

/**
 * Sequential execution interface
 */
export interface SequentialExecution {
  /** Request sequential execution */
  requestExecution: (reason?: string) => void
  /** Check if element has data and should refresh */
  hasDataAndShouldRefresh: () => boolean
}

/**
 * Configuration for useElementState hook
 */
export interface ElementStateConfig<TData = unknown> {
  /** Element ID */
  elementId: string
  /** Element type */
  elementType: ELEMENT_TYPE
  /** Query instance for data operations */
  queryInstance: QueryInstance | null
  /** Whether element is in dashboard mode */
  isDashboard?: boolean
  /** Auto-run configuration */
  autoRun?: Partial<AutoRunConfig>
  /** Initial data */
  initialData?: TData | null
  /** Initial show selects state */
  initialShowSelects?: boolean
  /** Callback for sequential execution requests */
  onExecutionRequest?: (
    elementId: string,
    executor: () => Promise<void>,
    reason: string
  ) => void
  /** Callback for element updates */
  onElementUpdate?: (
    elementId: string,
    updates: Record<string, unknown>
  ) => void
  /** Callback for delete requests */
  onDeleteRequest?: () => void
}

/**
 * Default auto-run configuration
 */
const DEFAULT_AUTO_RUN: AutoRunConfig = {
  enabled: true,
  delay: 100,
  maxAttempts: 3,
  conditions: () => true,
}

/**
 * Hook for standardized element state management
 *
 * Provides common patterns for:
 * - Element data, loading, and error state
 * - Configuration management
 * - Auto-run logic with conditions
 * - Sequential execution patterns
 * - Validation and initialization
 */
export const useElementState = <TData = unknown>(
  config: ElementStateConfig<TData>
) => {
  const {
    elementId,
    queryInstance,
    autoRun: autoRunConfig = {},
    initialData = null,
    initialShowSelects = true,
    onExecutionRequest,
    onElementUpdate,
    onDeleteRequest,
  } = config

  // Merge auto-run config with defaults
  const autoRun = useMemo(
    () => ({
      ...DEFAULT_AUTO_RUN,
      ...autoRunConfig,
    }),
    [autoRunConfig]
  )

  // Element state
  const [elementState, setElementState] = useState<ElementState<TData>>({
    data: initialData,
    isLoading: false,
    error: null,
    hasAttemptedLoad: false,
    hasProcessedData: false,
    isQueuedForExecution: false,
  })

  // Configuration state
  const [configState, setConfigState] = useState<ElementConfigState>({
    showSelects: initialShowSelects,
    isInitializing: true,
    skipNextConfigUpdate: false,
  })

  // Auto-run tracking
  const [autoRunAttempts, setAutoRunAttempts] = useState<number>(0)

  // Refs for preventing infinite loops and tracking state
  const isExecutingRef = useRef<boolean>(false)
  const hasAutoRunRef = useRef<boolean>(false)
  const elementExecutorRef = useRef<(() => Promise<void>) | null>(null)

  // State update helpers
  const updateElementState = useCallback(
    (updates: Partial<ElementState<TData>>) => {
      setElementState((prev) => ({ ...prev, ...updates }))
    },
    []
  )

  const updateConfigState = useCallback(
    (updates: Partial<ElementConfigState>) => {
      setConfigState((prev) => ({ ...prev, ...updates }))
    },
    []
  )

  // Error management
  const setError = useCallback(
    (error: string | null) => {
      updateElementState({ error })
    },
    [updateElementState]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [setError])

  // Loading management
  const setLoading = useCallback(
    (isLoading: boolean) => {
      updateElementState({ isLoading })
    },
    [updateElementState]
  )

  // Data management
  const setData = useCallback(
    (data: TData | null) => {
      updateElementState({
        data,
        hasProcessedData: true,
        error: data ? null : elementState.error, // Clear error only if we have data
      })
    },
    [updateElementState, elementState.error]
  )

  // Queue management
  const setQueuedForExecution = useCallback(
    (isQueued: boolean) => {
      updateElementState({ isQueuedForExecution: isQueued })
    },
    [updateElementState]
  )

  // Mark as attempted load
  const markAttemptedLoad = useCallback(() => {
    updateElementState({ hasAttemptedLoad: true })
    setQueuedForExecution(false)
  }, [updateElementState, setQueuedForExecution])

  // Sequential execution helper
  const requestSequentialExecution = useCallback(
    (reason: string = "user_initiated") => {
      if (onExecutionRequest && elementExecutorRef.current) {
        setQueuedForExecution(true)
        onExecutionRequest(elementId, elementExecutorRef.current, reason)
      }
    },
    [onExecutionRequest, elementId, setQueuedForExecution]
  )

  // Register element executor
  const registerExecutor = useCallback((executor: () => Promise<void>) => {
    elementExecutorRef.current = executor
  }, [])

  // Check if element has data and should refresh
  const hasDataAndShouldRefresh = useCallback((): boolean => {
    return !!(
      elementState.data &&
      !elementState.isLoading &&
      !elementState.error &&
      elementState.hasProcessedData
    )
  }, [elementState])

  // Reset element state
  const resetElement = useCallback(() => {
    setElementState({
      data: null,
      isLoading: false,
      error: null,
      hasAttemptedLoad: false,
      hasProcessedData: false,
      isQueuedForExecution: false,
    })
    setConfigState((prev) => ({
      ...prev,
      showSelects: true,
    }))
    setAutoRunAttempts(0)
    hasAutoRunRef.current = false
    clearError()
  }, [clearError])

  // Handle element refresh
  const handleRefresh = useCallback(() => {
    updateElementState({ hasProcessedData: false })
    updateConfigState({ skipNextConfigUpdate: true })
    requestSequentialExecution("user_refresh")
  }, [updateElementState, updateConfigState, requestSequentialExecution])

  // Handle element edit (reset to selection state)
  const handleEdit = useCallback(() => {
    resetElement()
    if (onElementUpdate) {
      // Reset to default dimensions if needed
      onElementUpdate(elementId, { width: undefined, height: undefined })
    }
  }, [resetElement, onElementUpdate, elementId])

  // Handle element delete
  const handleDelete = useCallback(() => {
    if (onDeleteRequest) {
      onDeleteRequest()
    }
  }, [onDeleteRequest])

  // Auto-run logic
  useEffect(() => {
    if (
      !autoRun.enabled ||
      hasAutoRunRef.current ||
      configState.isInitializing
    ) {
      return
    }

    if (autoRunAttempts >= autoRun.maxAttempts) {
      return
    }

    // Check auto-run conditions
    const shouldAutoRun =
      autoRun.conditions() &&
      !configState.showSelects &&
      !elementState.data &&
      !elementState.isLoading &&
      !elementState.error &&
      !elementState.hasProcessedData &&
      queryInstance &&
      elementExecutorRef.current

    if (shouldAutoRun) {
      hasAutoRunRef.current = true
      setAutoRunAttempts((prev) => prev + 1)

      const timer = setTimeout(() => {
        requestSequentialExecution("auto_run")
      }, autoRun.delay)

      return () => clearTimeout(timer)
    }
  }, [
    autoRun,
    autoRunAttempts,
    configState.isInitializing,
    configState.showSelects,
    elementState.data,
    elementState.isLoading,
    elementState.error,
    elementState.hasProcessedData,
    queryInstance,
    requestSequentialExecution,
  ])

  // Initialization effect
  useEffect(() => {
    const timer = setTimeout(() => {
      updateConfigState({ isInitializing: false })
    }, 50)

    return () => clearTimeout(timer)
  }, [updateConfigState])

  // Validation helpers
  const isReadyForExecution = useCallback((): boolean => {
    return !!(
      queryInstance &&
      !elementState.isLoading &&
      !elementState.isQueuedForExecution &&
      !isExecutingRef.current
    )
  }, [queryInstance, elementState.isLoading, elementState.isQueuedForExecution])

  const canShowData = useCallback((): boolean => {
    return !!(
      elementState.data &&
      elementState.hasProcessedData &&
      !configState.showSelects
    )
  }, [
    elementState.data,
    elementState.hasProcessedData,
    configState.showSelects,
  ])

  // Sequential execution interface
  const sequentialExecution: SequentialExecution = useMemo(
    () => ({
      requestExecution: requestSequentialExecution,
      hasDataAndShouldRefresh,
    }),
    [requestSequentialExecution, hasDataAndShouldRefresh]
  )

  return {
    // State
    elementState,
    configState,
    autoRunAttempts,

    // State updates
    updateElementState,
    updateConfigState,
    setData,
    setError,
    clearError,
    setLoading,
    setQueuedForExecution,
    markAttemptedLoad,

    // Actions
    resetElement,
    handleRefresh,
    handleEdit,
    handleDelete,

    // Execution
    registerExecutor,
    requestSequentialExecution,
    sequentialExecution,

    // Validation
    isReadyForExecution,
    canShowData,
    hasDataAndShouldRefresh,

    // Computed state
    isLoading: elementState.isLoading || elementState.isQueuedForExecution,
    hasData: !!elementState.data,
    hasError: !!elementState.error,
    showSelects: configState.showSelects,
    isInitializing: configState.isInitializing,
  }
}

export default useElementState
